import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { extractDocument } from '../../lib/docExtract';
import { renderMarkdown, renderMarkdownInline } from '../../lib/md';
import CapturesGallery, { fileUrl } from '../CapturesGallery';
import BarChart from '../charts/BarChart';
import CountUp from '../charts/CountUp';
import Donut from '../charts/Donut';
import LineChart from '../charts/LineChart';
import Radar from '../charts/Radar';
import Sankey from '../charts/Sankey';
import TooltipHost from '../charts/Tooltip';
import ChartSuggestions from './ChartSuggestions';
import ExportModal from './ExportModal';
import { THEMES, ThemeId, themeClass, normalizeThemeId, FONT_CHOICES, FontId, getFont, Orientation, CustomTheme } from './themes';
import { suggestionToSection } from '../../lib/suggestCharts';
import { formatCurrency, formatNumber, formatPercent } from '../../lib/fmt';
// Shared layout (grille 12, DnD, mode édition/présentation, pickers).
import {
  SlideWidth as SharedSlideWidth,
  SlideSpan as SharedSlideSpan,
  EditorMode,
  autoAdjustSpans,
  inferDefaultWidth,
  isValidWidth,
  spanToClass as sharedSpanToClass,
  toSpan as sharedToSpan,
} from './shared/layout';
import { PresentationCanvas, PresentationWrap, AutoSaveBadge } from './shared/PresentationCanvas';
import { OrientationPicker, ModePicker, ThemeSwatches } from './shared/Pickers';
import { useAutoSave } from './shared/useAutoSave';
import { exportHtmlStandalone as sharedExportHtml } from './shared/exportHtml';
import { useSyntheseTheme } from './shared/SyntheseContext';
import {
  IconBack,
  IconDownload,
  IconPlay,
  IconPlus,
  IconSave,
  IconSparkle,
  IconTrash,
} from './shared/icons';

// Ré-export pour compat : certaines pages (SharedPrintView) importent encore ces helpers ici.
export const spanToClass = sharedSpanToClass;
export const toSpan = sharedToSpan;
export type { SharedSlideWidth as SlideWidth, SharedSlideSpan as SlideSpan };

type DocRow = {
  id: number;
  title: string;
  status: string;
};

type DocDetail = {
  id: number;
  title: string;
  versions: Array<{
    id: number;
    filename: string;
    mime_type: string | null;
    file_key: string;
  }>;
};

// Type local : alias vers le type partagé pour éviter d'imposer un rename invasif.
type SlideWidth = SharedSlideWidth;

type Section = {
  id: string;
  type: 'hero' | 'kpi' | 'text' | 'chart' | 'capture' | 'table' | 'timeline' | 'quote';
  /** Largeur sur 12 colonnes (3/4/6/8/12). Legacy : 'full' | 'half' | 'third'. */
  width?: SlideWidth;
  title?: string;
  body?: string;
  kpis?: Array<{ label: string; value: string; trend?: string }>;
  chart?: {
    kind: 'donut' | 'bar' | 'line' | 'radar' | 'sankey';
    data: any[];
    xKey?: string;
    yKey?: string;
    label?: string;
    /** Séries multiples pour les comparaisons (prévu/réel, brut/net, plusieurs catégories). */
    series?: Array<{ key: string; label: string; color?: string }>;
    /** Unité pour le formatage des axes/tooltips (ex: "CHF", "m²", "%"). */
    unit?: string;
    /** Secondes série (objectifs) pour un radar. */
    targets?: any[];
    /** Barres empilées / horizontales. */
    stacked?: boolean;
    horizontal?: boolean;
  };
  capture_id?: number;
  table?: { columns: string[]; rows: string[][] };
  timeline?: { items: Array<{ date: string; title: string; text?: string }> };
  quote?: string;
};

type Presentation = {
  title: string;
  subtitle: string;
  sections: Section[];
  theme?: ThemeId;
  mode?: 'project' | 'internal';
  /** Orientation globale de la présentation. Portrait par défaut. */
  orientation?: Orientation;
  /** Config custom (masque PDF + police + couleur d'accent) quand theme = 'custom'. */
  custom?: CustomTheme;
};

type SavedPresentation = {
  id: number;
  title: string;
  kind: 'pilotage' | 'presentation' | 'composition';
  is_template: number;
  created_at: string;
  updated_at: string;
  author: string | null;
};

type Mode = 'project' | 'internal';

/** Normalise une réponse d'API en présentation valide (protège contre JSON incomplet de l'IA). */
/**
 * Signature d'une section pour la dédup : combine type + titre normalisé + "forme"
 * des données (kind + nb points + nb séries) pour repérer les redondances.
 * Ex : deux charts "Planning du projet" avec les mêmes jalons → signature identique.
 */
function sectionSignature(s: any): string {
  const t = String(s?.type ?? 'unknown');
  const title = String(s?.title ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (t === 'chart' && s?.chart) {
    const c = s.chart;
    const dataLen = Array.isArray(c?.data) ? c.data.length : 0;
    const seriesLen = Array.isArray(c?.series) ? c.series.length : 0;
    // Clé : kind + titre + nb points + nb séries (suffit pour repérer les doubles visualisations du même jeu)
    return `chart|${c.kind}|${title}|${dataLen}|${seriesLen}`;
  }
  if (t === 'timeline' && s?.timeline) {
    const n = Array.isArray(s.timeline.items) ? s.timeline.items.length : 0;
    return `timeline|${title}|${n}`;
  }
  if (t === 'table' && s?.table) {
    const n = Array.isArray(s.table.rows) ? s.table.rows.length : 0;
    return `table|${title}|${n}`;
  }
  if (t === 'kpi') return `kpi|${title}`;
  return `${t}|${title}`;
}

/**
 * Transforme une section problématique en section plus lisible AVANT rendu.
 *  - CFC % avec somme > 120 % ou < 80 % → convertit en chart en CHF si possible, sinon table
 *  - LOD en donut avec total qui n'a pas de sens métier → stacked bar par phase, sinon table simple
 *  - Chart avec items à unités hétérogènes (W/m²K + kWc + %) → table clé-valeur (une ligne par item)
 *
 * Mutations non destructives : on renvoie une NOUVELLE section si on transforme.
 */
function sanitizeSection(s: any): any {
  if (!s || typeof s !== 'object') return s;
  if (s.type !== 'chart' || !s.chart) return s;

  const chart = s.chart;
  const title = String(s.title ?? '').toLowerCase();
  const unit = String(chart.unit ?? '').trim();
  const data: any[] = Array.isArray(chart.data) ? chart.data : [];

  // --- CAS 1 : pourcentages CFC avec somme aberrante ---
  // L'IA produit parfois "100 % Gros œuvre" + "40 % Techniques" dont la somme n'a aucun sens (> 100).
  // Détection : unité "%", plus de 3 items, somme hors [80, 120].
  if ((unit === '%' || /%$/.test(String(chart.yKey ?? ''))) && data.length >= 3) {
    const sum = data.reduce((acc, d) => acc + (Number(d?.value) || 0), 0);
    if (sum > 120 || sum < 80) {
      // On ne peut pas recalculer la vérité sans le total absolu → on dégrade en table
      return {
        ...s,
        type: 'table',
        chart: undefined,
        table: {
          columns: ['Poste', 'Valeur', 'Unité'],
          rows: data.map((d) => [
            String(d?.label ?? d?.name ?? '—'),
            String(Number(d?.value) || 0),
            '%',
          ]),
        },
        body: `⚠ Répartition en % incohérente (somme = ${Math.round(sum)} %) — affichée en tableau brut. Vérifier les données source.`,
      };
    }
  }

  // --- CAS 2 : donut LOD (total sans sens métier) ---
  // "Progression LOD", "Niveau LOD par lot"… en donut → le "total" au centre n'a aucun sens.
  // On convertit en bar vertical, chaque barre = un phase/lot, valeur = LOD atteint.
  const isLodChart = /\blod\b/.test(title) || /\bniveau de d[ée]tail\b/.test(title);
  if (isLodChart && chart.kind === 'donut' && data.length >= 2) {
    return {
      ...s,
      chart: {
        ...chart,
        kind: 'bar',
        horizontal: false,
        label: 'LOD atteint',
      },
    };
  }
  // Si UN seul chiffre LOD → KPI plutôt que donut
  if (isLodChart && chart.kind === 'donut' && data.length === 1) {
    const d = data[0];
    return {
      ...s,
      type: 'kpi',
      chart: undefined,
      kpis: [
        {
          label: 'LOD atteint',
          value: String(d?.value ?? '—'),
          trend: d?.label ? `${d.label}` : undefined,
        },
      ],
    };
  }

  // --- CAS 3 : unités hétérogènes sur un seul axe ---
  // L'IA produit parfois un chart "Performances énergétiques" qui mélange W/m²K, kWc, m²…
  // Sur un seul axe Y c'est illisible → on dégrade en table clé-valeur.
  const perItemUnits = data
    .map((d: any) => (d?.unit ? String(d.unit).trim() : ''))
    .filter((u: string) => u.length > 0);
  const distinctUnits = new Set(perItemUnits);
  if (distinctUnits.size >= 2) {
    return {
      ...s,
      type: 'table',
      chart: undefined,
      table: {
        columns: ['Indicateur', 'Valeur', 'Unité'],
        rows: data.map((d: any) => [
          String(d?.label ?? '—'),
          String(d?.value ?? '—'),
          String(d?.unit ?? '—'),
        ]),
      },
    };
  }

  return s;
}

function normalizePresentation(p: any): Presentation {
  if (!p || typeof p !== 'object') {
    return { title: 'Sans titre', subtitle: '', sections: [] };
  }
  const raw = Array.isArray(p.sections) ? p.sections : [];
  // 1) On applique d'abord les sanitizers (transformation des sections problématiques).
  const sanitized = raw.map(sanitizeSection);
  // 2) Dédup : on garde la 1ʳᵉ occurrence de chaque signature. Protège aussi contre un hero dupliqué.
  const seen = new Set<string>();
  const deduped: any[] = [];
  for (const s of sanitized) {
    const sig = sectionSignature(s);
    if (seen.has(sig)) continue;
    seen.add(sig);
    deduped.push(s);
  }
  const theme = normalizeThemeId(p.theme);
  const custom = (p.custom && typeof p.custom === 'object') ? (p.custom as CustomTheme) : undefined;
  // Si le masque custom impose une orientation, elle l'emporte. Sinon on prend celle du payload, défaut portrait.
  const orientation: Orientation =
    custom?.mask_orientation ?? (p.orientation === 'landscape' ? 'landscape' : 'portrait');
  return {
    title: typeof p.title === 'string' ? p.title : 'Sans titre',
    subtitle: typeof p.subtitle === 'string' ? p.subtitle : '',
    sections: deduped.map((s: any, i: number) => ({
      id: typeof s?.id === 'string' ? s.id : `sec${i + 1}`,
      type: s?.type ?? 'text',
      ...(s ?? {}),
      width: isValidWidth(s?.width) ? s.width : inferDefaultWidth(s),
    })),
    theme,
    mode: p.mode,
    orientation,
    custom,
  };
}

export default function SynthPresentation({ projectId }: { projectId: number }) {
  const [mode, setMode] = useState<Mode>('project');
  // Thème partagé avec Composition via le contexte Synthèse.
  const { theme, setTheme } = useSyntheseTheme();
  // Mode d'affichage édition / présentation — local à cet onglet.
  const [editorMode, setEditorMode] = useState<EditorMode>('edition');

  const [docs, setDocs] = useState<DocRow[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<number>>(new Set());
  const [selectedCaptures, setSelectedCaptures] = useState<Set<number>>(new Set());
  const [instructions, setInstructions] = useState('');
  const [template, setTemplate] = useState<'technique' | 'synthese' | 'vitrine'>('synthese');

  const exportRef = useRef<HTMLDivElement>(null);

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ step: string; current: number; total: number } | null>(
    null
  );
  const [genError, setGenError] = useState<string | null>(null);
  const [presentation, setPresentation] = useState<Presentation | null>(null);
  const [fullscreen, setFullscreen] = useState<number | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  // Ref sur la présentation pour donner à useAutoSave une lecture à jour (évite les closures stales).
  const presentationRef = useRef<Presentation | null>(null);
  const currentIdRef = useRef<number | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [regenIdx, setRegenIdx] = useState<number | null>(null);
  const [savedList, setSavedList] = useState<SavedPresentation[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [currentId, setCurrentId] = useState<number | null>(null);
  // Extractions IA gardées pour proposer des visualisations à la Power BI
  const [extractionsRaw, setExtractionsRaw] = useState<any[]>([]);

  useEffect(() => {
    api
      .get<{ documents: DocRow[] }>(`/projects/${projectId}/documents`)
      .then((r) => setDocs(Array.isArray(r?.documents) ? r.documents : []))
      .catch(() => setDocs([]));
  }, [projectId]);

  async function refreshSaved() {
    setSavedLoading(true);
    try {
      const r = await api.get<{ syntheses: SavedPresentation[] }>(
        `/projects/${projectId}/synthesis`
      );
      const list = Array.isArray(r?.syntheses) ? r.syntheses : [];
      // On garde seulement les présentations (pas les gabarits Pilotage)
      setSavedList(list.filter((s) => s.kind === 'presentation'));
    } catch {
      setSavedList([]);
    } finally {
      setSavedLoading(false);
    }
  }
  useEffect(() => {
    refreshSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function loadSaved(id: number) {
    try {
      const r = await api.get<{ synthesis: { id: number; title: string; payload: any } }>(
        `/synthesis/${id}`
      );
      const p = normalizePresentation(r.synthesis.payload);
      // Si le payload n'a pas de titre, on garde celui de la sauvegarde
      if (!p.title || p.title === 'Sans titre') p.title = r.synthesis.title;
      setPresentation(p);
      setCurrentId(r.synthesis.id);
    } catch (e: any) {
      alert(`Chargement impossible : ${e?.message ?? e}`);
    }
  }

  async function deleteSaved(id: number) {
    if (!confirm('Supprimer cette présentation sauvegardée ?')) return;
    try {
      await api.del(`/synthesis/${id}`);
      if (currentId === id) {
        setCurrentId(null);
        setPresentation(null);
      }
      refreshSaved();
    } catch (e: any) {
      alert(`Suppression impossible : ${e?.message ?? e}`);
    }
  }

  async function generateProjectMode(): Promise<void> {
    const ids = [...selectedDocs];
    const capIds = [...selectedCaptures];
    if (ids.length === 0 && capIds.length === 0) {
      setGenError('Sélectionne au moins un document ou une capture.');
      return;
    }

    // 1) Charger les métadonnées de chaque document pour connaître le fichier à extraire
    setProgress({ step: 'Préparation…', current: 0, total: ids.length + 1 });
    const extractions: any[] = [];
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      setProgress({
        step: `Analyse du document ${i + 1}/${ids.length}…`,
        current: i,
        total: ids.length + 1,
      });
      try {
        const det = await api.get<{
          document: { id: number; title: string };
          versions: DocDetail['versions'];
        }>(`/documents/${id}`);
        const ver = det.versions[0];
        if (!ver) {
          extractions.push({
            document_title: det.document.title,
            note: 'aucune version déposée',
          });
          continue;
        }
        // Extraction texte côté client
        const ext = await extractDocument({
          filename: ver.filename,
          mime: ver.mime_type,
          fileUrl: `/api/v1/files?key=${encodeURIComponent(ver.file_key)}`,
        });
        // Envoi au worker pour analyse par Claude
        const analyzed = await api.post<{ extraction: any }>(
          `/projects/${projectId}/synthesis/analyze-doc`,
          {
            document_id: id,
            document_title: det.document.title,
            filename: ver.filename,
            extracted_text: ext.text,
            extraction_kind: ext.kind,
            template,
          }
        );
        extractions.push({
          document_id: id,
          document_title: det.document.title,
          ...analyzed.extraction,
        });
      } catch (e: any) {
        extractions.push({
          document_id: id,
          error: String(e?.message ?? e).slice(0, 200),
        });
      }
      // Délai 5s entre appels pour éviter le rate limit (sauf après le dernier)
      if (i < ids.length - 1) {
        setProgress({
          step: `Pause anti rate-limit (5 s)…`,
          current: i + 1,
          total: ids.length + 1,
        });
        await new Promise((r) => setTimeout(r, 5000));
      }
    }

    // 2) Synthèse finale
    setProgress({
      step: 'Composition de la présentation…',
      current: ids.length,
      total: ids.length + 1,
    });
    const r = await api.post<{ presentation: Presentation }>(
      `/projects/${projectId}/synthesis/presentation/from-extracts`,
      {
        extractions,
        capture_ids: capIds,
        instructions,
        template,
        theme,
      }
    );
    setPresentation(normalizePresentation(r.presentation));
    setCurrentId(null);
    setExtractionsRaw(extractions);
  }

  async function generateInternalMode(): Promise<void> {
    setProgress({ step: 'Lecture des métriques…', current: 0, total: 2 });
    const metrics = await api
      .get<any>(`/projects/${projectId}/synthesis/metrics`)
      .catch(() => null);
    setProgress({ step: 'Rédaction du rapport interne…', current: 1, total: 2 });
    const r = await api.post<{ presentation: Presentation }>(
      `/projects/${projectId}/synthesis/presentation/internal`,
      {
        instructions,
        template,
        capture_ids: [...selectedCaptures],
        metrics,
        theme,
      }
    );
    setPresentation(normalizePresentation(r.presentation));
    setCurrentId(null);
  }

  async function generate() {
    setGenerating(true);
    setPresentation(null);
    setGenError(null);
    setProgress(null);
    try {
      if (mode === 'project') {
        await generateProjectMode();
      } else {
        await generateInternalMode();
      }
    } catch (e: any) {
      const msg = String(e?.message ?? e);
      setGenError(
        msg.toLowerCase().includes('rate_limit') || msg.toLowerCase().includes('rate limit')
          ? 'Limite de taux atteinte sur l\'API Claude. Attends 1 minute et relance, ou réduis le nombre de documents.'
          : msg
      );
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  }

  async function regenSection(idx: number) {
    if (!presentation) return;
    const sec = presentation.sections[idx];
    const instr = prompt(
      'Comment régénérer cette section ? (laisse vide pour une simple amélioration)',
      ''
    );
    if (instr === null) return;
    setRegenIdx(idx);
    try {
      const r = await api.post<{ section: Section }>(
        `/projects/${projectId}/synthesis/presentation/regenerate-section`,
        {
          section: sec,
          instructions: instr || 'Améliore cette section (style, concision).',
          context: { title: presentation.title, subtitle: presentation.subtitle, mode: presentation.mode },
        }
      );
      const next = { ...presentation, sections: [...presentation.sections] };
      next.sections[idx] = { ...r.section, id: sec.id };
      setPresentation(next);
    } catch (e: any) {
      alert(`Erreur : ${e.message ?? e}`);
    } finally {
      setRegenIdx(null);
    }
  }

  function updateSection(idx: number, patch: Partial<Section>) {
    if (!presentation) return;
    const next = { ...presentation, sections: [...presentation.sections] };
    next.sections[idx] = { ...next.sections[idx], ...patch };
    setPresentation(next);
  }

  function removeSection(idx: number) {
    if (!presentation) return;
    const filtered = (presentation.sections ?? []).filter((_, i) => i !== idx);
    setPresentation({ ...presentation, sections: autoAdjustSpans(filtered) });
  }

  function addTextSection() {
    if (!presentation) return;
    const withNew = [
      ...presentation.sections,
      {
        id: 's' + Date.now(),
        type: 'text' as const,
        title: 'Nouvelle section',
        body: 'Texte libre…',
      },
    ];
    setPresentation({ ...presentation, sections: autoAdjustSpans(withNew) });
  }

  function moveSection(idx: number, dir: -1 | 1) {
    if (!presentation) return;
    const j = idx + dir;
    if (j < 0 || j >= (presentation.sections ?? []).length) return;
    const nextSections = [...presentation.sections];
    [nextSections[idx], nextSections[j]] = [nextSections[j], nextSections[idx]];
    setPresentation({ ...presentation, sections: autoAdjustSpans(nextSections) });
  }

  function changeTheme(t: ThemeId) {
    setTheme(t);
    if (presentation) setPresentation({ ...presentation, theme: t });
  }

  function addSuggestion(s: any) {
    if (!presentation) return;
    const section = suggestionToSection(s);
    setPresentation({ ...presentation, sections: [...(presentation.sections ?? []), section] });
  }

  async function save() {
    if (!presentation) return;
    const title = prompt('Titre de la présentation', presentation.title) ?? presentation.title;
    try {
      const r = await api.post<{ id: number }>(`/projects/${projectId}/synthesis`, {
        id: currentId ?? undefined,
        kind: 'presentation',
        title,
        payload: { ...presentation, title },
      });
      setCurrentId(r.id);
      setPresentation({ ...presentation, title });
      refreshSaved();
    } catch (e: any) {
      alert(`Sauvegarde impossible : ${e?.message ?? e}`);
    }
  }

  // Refs synchrones : useAutoSave lit toujours l'état le + récent via latestFn interne.
  useEffect(() => { presentationRef.current = presentation; }, [presentation]);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);

  // Auto-save debounced 1500 ms, appelé par le canvas à chaque modif de layout.
  const { status: saveStatus, trigger: scheduleAutoSave } = useAutoSave(async () => {
    const p = presentationRef.current;
    if (!p) return;
    const r = await api.post<{ id: number }>(`/projects/${projectId}/synthesis`, {
      id: currentIdRef.current ?? undefined,
      kind: 'presentation',
      title: p.title,
      payload: p,
    });
    if (currentIdRef.current !== r.id) setCurrentId(r.id);
    refreshSaved();
  });

  /**
   * Export PDF via le moteur d'impression natif du navigateur.
   * Une feuille @media print masque tout SAUF #presentation-export-target,
   * ce qui produit un PDF vectoriel net (texte sélectionnable, pas de pixellisation).
   * L'utilisateur valide via la dialog d'impression et choisit "Enregistrer en PDF".
   */
  /**
   * Export PDF via window.print() — fallback gratuit, mais affiche la dialog Chrome
   * avec ses headers/footers. L'utilisateur doit les décocher manuellement.
   */
  async function exportPdf() {
    if (!presentation) return;
    setExportOpen(false);
    document.documentElement.classList.add('print-presentation-only');
    await new Promise((r) => setTimeout(r, 80));
    try {
      window.print();
    } finally {
      setTimeout(() => {
        document.documentElement.classList.remove('print-presentation-only');
      }, 200);
    }
  }


  /** Export HTML autonome — délègue au helper partagé (shared/exportHtml). */
  async function exportHtmlStandalone(
    opts: {
      include3D: boolean;
      viewer_mode?: 'none' | 'static' | 'interactive';
      viewer_link_expires_days?: number;
    } = { include3D: true, viewer_mode: 'static' }
  ) {
    if (!presentation || !exportRef.current) return;
    await sharedExportHtml({
      exportEl: exportRef.current,
      title: presentation.title || 'Présentation',
      projectId,
      opts,
    });
    setExportOpen(false);
  }

  const effectiveTheme = presentation?.theme ?? theme;

  return (
    <>
      <TooltipHost />
      {!presentation && (savedList.length > 0 || savedLoading) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>Mes présentations sauvegardées</strong>
            <span className="card-meta" style={{ fontSize: 11 }}>
              {savedList.length} enregistrée{savedList.length > 1 ? 's' : ''}
            </span>
            <button
              className="btn btn-ghost"
              style={{ padding: '2px 10px', fontSize: 11, marginLeft: 'auto' }}
              onClick={refreshSaved}
              disabled={savedLoading}
              title="Actualiser"
              aria-label="Actualiser"
            >
              <IconSparkle />
            </button>
          </div>
          <div className="saved-grid">
            {savedList.map((s) => (
              <div key={s.id} className="saved-card">
                <div className="saved-card-body" onClick={() => loadSaved(s.id)}>
                  <div className="saved-title">{s.title}</div>
                  <div className="saved-meta">
                    {new Date(s.updated_at.replace(' ', 'T') + 'Z').toLocaleDateString('fr-CH')}
                    {s.author ? ` · ${s.author}` : ''}
                  </div>
                </div>
                <div className="saved-actions">
                  <button
                    className="btn btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => loadSaved(s.id)}
                  >
                    Ouvrir
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11, color: '#b84b2a' }}
                    onClick={() => deleteSaved(s.id)}
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!presentation && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <strong style={{ fontSize: 15 }}>Créer une présentation</strong>
            <div className="card-meta" style={{ fontSize: 12 }}>
              Choisis d'abord ce que tu veux présenter, puis un style visuel.
            </div>
          </div>

          {/* ----- Mode selector ----- */}
          <div className="mode-selector">
            <button
              className={`mode-card ${mode === 'project' ? 'active' : ''}`}
              onClick={() => setMode('project')}
            >
              <div className="mc-title">Présenter le projet</div>
              <div className="mc-desc">
                L'IA lit les documents (PDF, rapports, métrés) et en extrait les informations sur
                l'ouvrage : type, surface, matériaux, quantités, planning, intervenants. Pour les
                partenaires, le MO ou le public.
              </div>
            </button>
            <button
              className={`mode-card ${mode === 'internal' ? 'active' : ''}`}
              onClick={() => setMode('internal')}
            >
              <div className="mc-title">État interne / Workflow</div>
              <div className="mc-desc">
                Rapport d'avancement interne : docs validés, tâches en cours, activité, délais.
                Pour l'équipe et la hiérarchie. Ne sort pas à l'extérieur.
              </div>
            </button>
          </div>

          {/* ----- Theme picker ----- */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>
              Style visuel
            </label>
            <div className="theme-picker">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  className={`theme-tile ${theme === t.id ? 'active' : ''}`}
                  onClick={() => changeTheme(t.id)}
                >
                  <div className={`theme-preview preview-${t.id}`} />
                  <div className="lbl">{t.label}</div>
                  <div className="sub">{t.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ----- Form spécifique au mode ----- */}
          {mode === 'project' ? (
            <>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600 }}>
                  Documents à analyser ({selectedDocs.size} sélectionnés)
                </label>
                <div className="card-meta" style={{ fontSize: 11, marginBottom: 4 }}>
                  PDFs, rapports techniques, Excel de métrés… L'IA va lire leur contenu pour en
                  extraire les infos du projet.
                </div>
                <div
                  style={{
                    border: '1px solid var(--c-border)',
                    borderRadius: 8,
                    padding: 8,
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}
                >
                  {docs.length === 0 && (
                    <div className="card-meta" style={{ fontSize: 12 }}>
                      Aucun document dans ce projet.
                    </div>
                  )}
                  {docs.map((d) => (
                    <label
                      key={d.id}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        padding: '3px 0',
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(d.id)}
                        onChange={(e) => {
                          const next = new Set(selectedDocs);
                          if (e.target.checked) next.add(d.id);
                          else next.delete(d.id);
                          setSelectedDocs(next);
                        }}
                      />
                      {d.title}
                      <span className="card-meta" style={{ fontSize: 11, marginLeft: 'auto' }}>
                        {d.status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div
              className="card-meta"
              style={{
                fontSize: 12,
                padding: 10,
                background: 'var(--c-nav)',
                borderRadius: 8,
              }}
            >
              Les métriques de workflow du projet seront automatiquement chargées (statuts des
              docs, tâches, activité, KPIs). Aucun document source n'est envoyé à l'IA dans ce mode.
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Captures 3D ({selectedCaptures.size} sélectionnées)
            </label>
            <div className="card-meta" style={{ fontSize: 11, marginBottom: 4 }}>
              Visuels du bâtiment à intégrer dans la présentation.
            </div>
            <button
              className="btn btn-ghost"
              onClick={() => setShowPicker((v) => !v)}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              {showPicker ? 'Masquer la galerie' : 'Parcourir la galerie'}
            </button>
            {showPicker && (
              <div style={{ marginTop: 8 }}>
                <CapturesGallery
                  projectId={projectId}
                  selectable
                  selectedIds={selectedCaptures}
                  onToggleSelect={(id) => {
                    const next = new Set(selectedCaptures);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    setSelectedCaptures(next);
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Instructions pour l'IA</label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder={
                mode === 'project'
                  ? 'Ex : Met en avant les surfaces, les matériaux et la particularité structurelle du projet.'
                  : 'Ex : Focus sur les retards et les documents à valider cette semaine.'
              }
              style={{
                width: '100%',
                marginTop: 4,
                padding: 8,
                borderRadius: 8,
                border: '1px solid var(--c-border)',
                background: 'var(--c-bg)',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Public cible</label>
            {[
              { k: 'technique', lbl: 'Technique (BE)' },
              { k: 'synthese', lbl: 'Synthèse (MO)' },
              { k: 'vitrine', lbl: 'Vitrine (public)' },
            ].map((t) => (
              <button
                key={t.k}
                className={`btn ${template === t.k ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTemplate(t.k as any)}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                {t.lbl}
              </button>
            ))}
            <button
              className="btn btn-primary"
              onClick={generate}
              disabled={generating}
              style={{ marginLeft: 'auto' }}
            >
              {generating ? 'Génération en cours…' : 'Générer la présentation'}
            </button>
          </div>
          {generating && progress && (
            <div
              style={{
                background: 'var(--c-nav)',
                borderRadius: 10,
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{progress.step}</span>
                <span className="card-meta">
                  {progress.current}/{progress.total}
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  background: 'var(--c-border)',
                  borderRadius: 100,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    background: 'var(--c-sage)',
                    width: `${Math.round((progress.current / Math.max(1, progress.total)) * 100)}%`,
                    transition: 'width 400ms ease',
                  }}
                />
              </div>
            </div>
          )}
          {genError && (
            <div
              className="error-msg"
              style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-start', gap: 8 }}
            >
              <span aria-hidden="true" style={{ fontWeight: 700 }}>!</span>
              <div style={{ flex: 1 }}>{genError}</div>
              <button
                className="btn btn-ghost"
                style={{ padding: '2px 10px', fontSize: 12 }}
                onClick={() => setGenError(null)}
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {presentation && (
        <>
          <div
            className="card pres-toolbar"
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <strong style={{ fontSize: 14 }}>{presentation.title}</strong>
            <span className="card-meta" style={{ fontSize: 11 }}>
              {(presentation.sections ?? []).length} section{(presentation.sections ?? []).length > 1 ? 's' : ''}
            </span>
            <AutoSaveBadge status={saveStatus} />

            <ThemeSwatches value={effectiveTheme} onChange={(t) => changeTheme(t)} />

            {/* Sélecteur d'orientation globale — verrouillé si masque custom impose une orientation */}
            <OrientationPicker
              value={presentation.orientation ?? 'portrait'}
              locked={
                effectiveTheme === 'custom' &&
                !!presentation.custom?.mask_orientation
                  ? presentation.custom!.mask_orientation!
                  : null
              }
              onChange={(o) => { setPresentation({ ...presentation, orientation: o }); scheduleAutoSave(); }}
            />

            {/* Bascule Édition / Présentation */}
            <ModePicker value={editorMode} onChange={setEditorMode} />

            {/* Bouton thème custom : ouvre un drawer d'upload PDF */}
            {effectiveTheme === 'custom' && (
              <button
                className="btn btn-ghost slide-tool-btn"
                onClick={() => setCustomOpen(true)}
                title="Configurer le thème custom (masque PDF, police, couleur)"
              >
                Config
              </button>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => { setPresentation(null); setCurrentId(null); }}>
                <IconBack /> Nouveau
              </button>
              <button className="btn btn-ghost" onClick={save}>
                <IconSave /> {currentId ? 'Mettre à jour' : 'Sauvegarder'}
              </button>
              <button className="btn btn-ghost" onClick={addTextSection}>
                <IconPlus /> Section
              </button>
              <button className="btn btn-ghost" onClick={() => setFullscreen(0)}>
                <IconPlay /> Présenter
              </button>
              <button className="btn btn-primary" onClick={() => setExportOpen(true)}>
                <IconDownload /> Exporter
              </button>
            </div>
          </div>

          {extractionsRaw.length > 0 && (
            <ChartSuggestions extractions={extractionsRaw} onAdd={addSuggestion} />
          )}

          <PresentationWrap
            innerRef={exportRef}
            themeClassName={themeClass(effectiveTheme)}
            orientation={presentation.orientation ?? 'portrait'}
            customPreview={effectiveTheme === 'custom' && !!presentation.custom?.mask_key}
            customStyle={
              effectiveTheme === 'custom'
                ? ({
                    '--custom-accent': presentation.custom?.accent ?? '#3D5A45',
                    '--custom-font': getFont(presentation.custom?.font),
                  } as React.CSSProperties)
                : undefined
            }
          >
            <PresentationCanvas
              sections={presentation.sections ?? []}
              mode={editorMode}
              regenIdx={regenIdx}
              renderContent={({ section, onEdit }) => <SlideContent section={section} onEdit={onEdit} />}
              onReorder={(next) => {
                setPresentation({ ...presentation, sections: next });
                scheduleAutoSave();
              }}
              onMoveUp={(i) => { moveSection(i, -1); scheduleAutoSave(); }}
              onMoveDown={(i) => { moveSection(i, 1); scheduleAutoSave(); }}
              onRegen={(i) => regenSection(i)}
              onEdit={(i, patch) => updateSection(i, patch)}
              onDelete={(i) => { removeSection(i); scheduleAutoSave(); }}
              onWidthChange={(i, w) => { updateSection(i, { width: w }); scheduleAutoSave(); }}
            />
          </PresentationWrap>

          {customOpen && (
            <CustomThemeDrawer
              projectId={projectId}
              current={presentation.custom ?? {}}
              onClose={() => setCustomOpen(false)}
              onChange={(next) => setPresentation({
                ...presentation,
                custom: next,
                orientation: next.mask_orientation ?? presentation.orientation ?? 'portrait',
              })}
            />
          )}
        </>
      )}

      {fullscreen !== null && presentation && (
        <FullscreenPresenter
          presentation={presentation}
          theme={effectiveTheme}
          start={fullscreen}
          onClose={() => setFullscreen(null)}
        />
      )}

      {exportOpen && presentation && (
        <ExportModal
          projectId={projectId}
          title={presentation.title}
          kind="presentation"
          payload={{ presentation }}
          onClose={() => setExportOpen(false)}
          onPdf={exportPdf}
          onHtml={exportHtmlStandalone}
        />
      )}
    </>
  );
}

/**
 * Conteneur DnD de la liste des slides. @dnd-kit/core + @dnd-kit/sortable.
 *
 * Comportements :
 *   - Drag handle en partie gauche (icône grip). On peut saisir un bloc et le déplacer.
 *   - Au drop, on réordonne via arrayMove, puis on applique un auto-ajustement :
 *     chaque "ligne logique" (groupe de blocs consécutifs dont la somme des spans
 *     tient dans 12) est rééquilibrée pour que tous les blocs d'une ligne aient le
 *     même span (12 / n). Hero exclu du rééquilibrage.
 */
// SortableSlides, SortableSlide, autoAdjustSpans, icônes — extraits dans shared/.

export function SlideContent({
  section,
  onEdit,
  fullscreen = false,
}: {
  section: Section;
  onEdit?: (p: Partial<Section>) => void;
  fullscreen?: boolean;
}) {
  // L'édition inline est désactivée pour rendre le markdown ; on garde onEdit
  // dans la signature pour compat appelants, mais on ne l'utilise plus ici.
  void onEdit;

  if (section.type === 'hero') {
    return (
      <div>
        <h1
          dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.title ?? '') }}
        />
        {section.body && (
          <p dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.body) }} />
        )}
      </div>
    );
  }
  if (section.type === 'kpi') {
    const kpis = Array.isArray(section.kpis) ? section.kpis : [];
    return (
      <div>
        {section.title && (
          <h2
            style={{ marginTop: 0 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.title) }}
          />
        )}
        <div className="slide-kpis">
          {kpis.map((k, i) => (
            <div key={i} className="slide-kpi">
              <div
                className="kpi-lbl"
                dangerouslySetInnerHTML={{ __html: renderMarkdownInline(k?.label ?? '') }}
              />
              <div className="kpi-val">
                <AnimatedKpi value={String(k?.value ?? '')} />
              </div>
              {k?.trend && (
                <div
                  className="kpi-trend"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownInline(k.trend) }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (section.type === 'text') {
    return (
      <div>
        {section.title && (
          <h2
            style={{ marginTop: 0 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.title) }}
          />
        )}
        <div
          className="slide-md-body"
          style={{ lineHeight: 1.7, fontSize: fullscreen ? 18 : 15 }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(section.body ?? '') }}
        />
      </div>
    );
  }
  if (section.type === 'chart' && section.chart) {
    return (
      <div>
        {section.title && <h2 style={{ marginTop: 0 }}>{section.title}</h2>}
        <ChartRenderer chart={section.chart} title={section.title} />
      </div>
    );
  }
  if (section.type === 'capture' && section.capture_id) {
    return (
      <div className="slide-capture">
        {section.title && (
          <h2
            style={{ marginTop: 0 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.title) }}
          />
        )}
        <CaptureResolver id={section.capture_id} />
        {section.body && (
          <div
            className="card-meta slide-md-body"
            style={{ marginTop: 10 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.body) }}
          />
        )}
      </div>
    );
  }
  if (section.type === 'table' && section.table) {
    return (
      <div>
        {section.title && <h2 style={{ marginTop: 0 }}>{section.title}</h2>}
        <FilterableTable
          cols={Array.isArray(section.table.columns) ? section.table.columns : []}
          rows={Array.isArray(section.table.rows) ? section.table.rows : []}
        />
      </div>
    );
  }
  if (section.type === 'timeline' && section.timeline) {
    const items = Array.isArray(section.timeline.items) ? section.timeline.items : [];
    return (
      <div>
        {section.title && (
          <h2
            style={{ marginTop: 0 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownInline(section.title) }}
          />
        )}
        <div className="slide-timeline">
          {items.map((it, i) => (
            <div key={i} className="tl-item">
              <div className="tl-date">{it?.date ?? ''}</div>
              <div
                className="tl-title"
                dangerouslySetInnerHTML={{ __html: renderMarkdownInline(it?.title ?? '') }}
              />
              {it?.text && (
                <div
                  className="slide-md-body"
                  style={{ fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: renderMarkdownInline(it.text) }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (section.type === 'quote') {
    const text = String(section.quote ?? section.body ?? '');
    return (
      <blockquote
        style={{
          fontSize: 22,
          fontStyle: 'italic',
          borderLeft: '4px solid var(--c-sage)',
          paddingLeft: 20,
          margin: 0,
          lineHeight: 1.5,
        }}
        dangerouslySetInnerHTML={{ __html: renderMarkdownInline(text) }}
      />
    );
  }
  return <div className="card-meta">Section vide</div>;
}

function AnimatedKpi({ value }: { value: string }) {
  const match = value.match(/^(-?\d+[\d.,]*)(\s*.*)$/);
  if (match) {
    const num = Number(match[1].replace(',', '.'));
    if (!Number.isNaN(num)) {
      return (
        <>
          <CountUp value={num} />
          {match[2]}
        </>
      );
    }
  }
  return <>{value}</>;
}

/** Nettoie une valeur numérique éventuellement stringifiée ("1 200 CHF", "-13%", "58 %"). */
function toNumericSafe(v: any): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;
  const cleaned = String(v).replace(/[^\d.,-]/g, '').replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

const SERIES_COLORS = [
  'var(--chart-c1)',
  'var(--chart-c2)',
  'var(--chart-c3)',
  'var(--chart-c4)',
  'var(--chart-c5)',
  'var(--chart-c6)',
];

/**
 * Construit un libellé humain pour la série UNIQUE d'un chart.
 * Priorité : chart.label explicite > clé humanisée > unit-based ("Valeur (m²)") > "Valeur".
 * Interdiction d'afficher "value" en dur.
 */
function buildPrimarySeriesLabel(
  chart: { label?: string; unit?: string; yKey?: string; title?: string },
  key: string
): string {
  const explicit = typeof chart.label === 'string' ? chart.label.trim() : '';
  if (explicit && explicit.toLowerCase() !== 'value' && explicit !== chart.unit) return explicit;
  const humanKey = humanizeSeriesKey(key);
  if (humanKey && humanKey.toLowerCase() !== 'valeur' && humanKey.toLowerCase() !== 'value') {
    return chart.unit ? `${humanKey} (${chart.unit})` : humanKey;
  }
  // Déduit depuis le titre : "Durée par phase" → "Durée"
  const title = typeof chart.title === 'string' ? chart.title : '';
  const mTitle = title.match(/^([A-Za-zÀ-ÿ'’\- ]+?)\s+par\s+/i);
  if (mTitle) {
    const base = mTitle[1].trim();
    return chart.unit ? `${base} (${chart.unit})` : base;
  }
  if (chart.unit) return `Valeur (${chart.unit})`;
  return 'Valeur';
}

/**
 * Détecte automatiquement les séries numériques dans des points hétérogènes
 * (ex: [{mois:"Jan", prevu:5, reel:3}] → séries "prevu" & "reel").
 * Si `xKey`/`yKey` sont fournis et que la donnée a bien une seule série, on s'y tient.
 */
function detectSeries(
  points: any[],
  xKey: string | undefined,
  yKey: string | undefined,
  explicit: Array<{ key: string; label: string; color?: string }> | undefined,
  chartHint: { label?: string; unit?: string; title?: string }
) {
  if (explicit && explicit.length > 0) return explicit;
  if (!points.length) return [{ key: 'value', label: buildPrimarySeriesLabel({ ...chartHint, yKey }, 'value') }];
  const reservedKeys = new Set(['label', 'key', 'axis', 'name', 'category', 'id', xKey ?? '']);
  if (yKey) {
    reservedKeys.delete(yKey);
    return [{ key: yKey, label: buildPrimarySeriesLabel({ ...chartHint, yKey }, yKey) }];
  }
  // Collecte les clés dont toutes (ou la majorité) des valeurs sont numériques
  const keyStats: Record<string, { numeric: number; total: number }> = {};
  for (const p of points) {
    if (!p || typeof p !== 'object') continue;
    for (const k of Object.keys(p)) {
      if (reservedKeys.has(k)) continue;
      if (!keyStats[k]) keyStats[k] = { numeric: 0, total: 0 };
      keyStats[k].total++;
      const v = p[k];
      if (typeof v === 'number') keyStats[k].numeric++;
      else if (typeof v === 'string' && /\d/.test(v) && Number.isFinite(toNumericSafe(v))) keyStats[k].numeric++;
    }
  }
  const numericKeys = Object.entries(keyStats)
    .filter(([, s]) => s.numeric / Math.max(1, s.total) >= 0.6)
    .map(([k]) => k);
  if (numericKeys.length === 0)
    return [{ key: 'value', label: buildPrimarySeriesLabel(chartHint, 'value') }];
  if (numericKeys.length === 1)
    return [{ key: numericKeys[0], label: buildPrimarySeriesLabel(chartHint, numericKeys[0]) }];
  return numericKeys.map((k) => ({ key: k, label: humanizeSeriesKey(k) }));
}

/** "prevu" → "Prévu", "reel" → "Réel", "cost_chf" → "Cost chf". */
function humanizeSeriesKey(k: string): string {
  const map: Record<string, string> = {
    prevu: 'Prévu',
    prévu: 'Prévu',
    reel: 'Réel',
    réel: 'Réel',
    planifie: 'Planifié',
    planifié: 'Planifié',
    realise: 'Réalisé',
    réalisé: 'Réalisé',
    objectif: 'Objectif',
    cible: 'Cible',
    cibles: 'Cibles',
    ecart: 'Écart',
    écart: 'Écart',
    brut: 'Brut',
    brute: 'Brute',
    net: 'Net',
    nette: 'Nette',
    surface_brute: 'Brute',
    surface_nette: 'Nette',
    budget: 'Budget',
    depense: 'Dépense',
    dépense: 'Dépense',
    total: 'Total',
    value: 'Valeur',
    value1: 'Série 1',
    value2: 'Série 2',
  };
  const low = k.toLowerCase();
  if (map[low]) return map[low];
  return k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');
}

/** Convertit un numéro de série Excel (ex. 45717) en "mai 2025". */
function isExcelSerialDate(v: any): boolean {
  if (typeof v !== 'number' && typeof v !== 'string') return false;
  const n = typeof v === 'number' ? v : Number(v);
  // Excel serials typiques 2020-2030 : ~43 800 (2020) à 47 500 (2030)
  return Number.isFinite(n) && Number.isInteger(n) && n >= 40000 && n <= 60000;
}
function formatExcelSerialDate(n: number): string {
  // Excel epoch = 1899-12-30 (avec bug 1900 année bissextile compensé en retirant 2 jours)
  const ms = (n - 25569) * 86400 * 1000;
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return String(n);
  return d.toLocaleDateString('fr-CH', { month: 'short', year: 'numeric' });
}

/** Wrapper chart : taille fixe. 'kpi' | 'compact' | 'std' (défaut) | 'large'. */
function ChartBox({
  children,
  className,
  size = 'std',
}: {
  children: React.ReactNode;
  className?: string;
  size?: 'kpi' | 'compact' | 'std' | 'large';
}) {
  const sizeCls = `chart-h-${size}`;
  return <div className={`slide-chart-wrap ${sizeCls}${className ? ' ' + className : ''}`}>{children}</div>;
}

function ChartRenderer({ chart, title }: { chart: NonNullable<Section['chart']>; title?: string }) {
  void title;
  const safeData: any[] = Array.isArray(chart.data) ? chart.data.filter(Boolean) : [];

  if (chart.kind === 'donut') {
    const donutData = safeData.map((d: any, i: number) => ({
      key: String(d.key ?? d.label ?? i),
      label: String(d.label ?? d.key ?? ''),
      value: toNumericSafe(d.value),
    }));
    const total = donutData.reduce((s, d) => s + d.value, 0);
    if (total <= 0) {
      return <ChartBox><EmptyState label="Aucune donnée à afficher pour ce camembert." /></ChartBox>;
    }
    // Plus de 10 segments → illisible en camembert. Bascule en barres horizontales triées.
    if (donutData.length > 10) {
      const sorted = [...donutData].sort((a, b) => b.value - a.value);
      return (
        <ChartBox>
          <BarChart
            data={sorted.map((d) => ({ label: d.label, value: d.value }))}
            series={[{ key: 'value', label: buildPrimarySeriesLabel({ label: chart.label, unit: chart.unit, title: (chart as any).title }, 'value') }]}
            unit={chart.unit ?? null}
            horizontal
            height={Math.max(240, sorted.length * 22)}
          />
        </ChartBox>
      );
    }
    return (
      <ChartBox className="chart-circular">
        <Donut data={donutData} size={240} thickness={56} unit={chart.unit} />
      </ChartBox>
    );
  }

  if (chart.kind === 'bar') {
    const xKey = chart.xKey ?? 'label';
    const chartHint = { label: chart.label, unit: chart.unit, title: (chart as any).title };
    const series = detectSeries(safeData, xKey, chart.yKey, chart.series, chartHint).map((s, i) => ({
      ...s,
      color: s.color ?? SERIES_COLORS[i % SERIES_COLORS.length],
    }));
    const mapped = safeData.map((d: any, i: number) => {
      const rawLabel = d?.label ?? d?.[xKey] ?? d?.name ?? `#${i + 1}`;
      // Excel serial date → "mai 2025" (évite les 45717 bruts sur le Planning).
      const label = isExcelSerialDate(rawLabel)
        ? formatExcelSerialDate(Number(rawLabel))
        : String(rawLabel);
      const out: any = { label };
      for (const s of series) {
        out[s.key] = toNumericSafe(d?.[s.key] ?? (s.key === 'value' ? d?.value : undefined));
      }
      return out;
    });
    const allZero = mapped.every((d) => series.every((s) => (Number(d[s.key]) || 0) === 0));
    if (mapped.length === 0 || allZero) {
      return <ChartBox><EmptyState label="Aucune donnée à afficher pour ce graphique." /></ChartBox>;
    }
    return (
      <ChartBox>
        <BarChart
          data={mapped}
          series={series}
          unit={chart.unit ?? null}
          stacked={!!chart.stacked}
          horizontal={!!chart.horizontal}
          height={280}
        />
      </ChartBox>
    );
  }

  if (chart.kind === 'line') {
    const xKey = chart.xKey ?? 'x';
    const yKey = chart.yKey ?? 'y';
    const chartHint = { label: chart.label, unit: chart.unit, title: (chart as any).title };
    const series = detectSeries(safeData, xKey, yKey, chart.series, chartHint).map((s, i) => ({
      ...s,
      color: s.color ?? SERIES_COLORS[i % SERIES_COLORS.length],
    }));
    const mapped = safeData.map((d: any) => {
      const out: any = { ...d, [xKey]: d?.[xKey] ?? d?.label ?? '' };
      for (const s of series) {
        out[s.key] = toNumericSafe(d?.[s.key] ?? (s.key === yKey ? d?.[yKey] : undefined) ?? d?.value);
      }
      return out;
    });
    // Si l'axe X contient des numéros de série Excel (IA a oublié de formatter), on les convertit à la volée
    const xValuesLookExcel = mapped.length > 0 && mapped.every((d) => isExcelSerialDate(d[xKey]));
    const xLabelFormat = xValuesLookExcel
      ? (v: any) => formatExcelSerialDate(Number(v))
      : undefined;
    if (mapped.length === 0) {
      return <ChartBox><EmptyState label="Aucune donnée temporelle à afficher." /></ChartBox>;
    }
    return (
      <ChartBox>
        <LineChart
          data={mapped}
          xKey={xKey}
          series={series}
          unit={chart.unit ?? null}
          xLabelFormat={xLabelFormat}
        />
      </ChartBox>
    );
  }

  if (chart.kind === 'radar') {
    const mapped = safeData.map((d: any, i: number) => ({
      axis: String(d?.axis ?? d?.label ?? `Axe ${i + 1}`),
      value: toNumericSafe(d?.value ?? d?.reel),
    }));
    const hasInlineObjective = safeData.some((d: any) => d?.objectif != null || d?.target != null || d?.cible != null);
    const inlineTargets = hasInlineObjective
      ? safeData.map((d: any, i: number) => ({
          axis: mapped[i]?.axis ?? String(d?.axis ?? `Axe ${i + 1}`),
          value: toNumericSafe(d?.objectif ?? d?.target ?? d?.cible),
        }))
      : undefined;
    const targets = Array.isArray(chart.targets)
      ? chart.targets.map((d: any, i: number) => ({
          axis: String(d?.axis ?? d?.label ?? mapped[i]?.axis ?? `Axe ${i + 1}`),
          value: toNumericSafe(d?.value ?? d?.objectif),
        }))
      : inlineTargets;
    // Radar 100 % vide (toutes les valeurs à 0 ou absentes) → état métier explicite
    const allZero = mapped.length === 0 || mapped.every((p) => !p.value);
    if (allZero) {
      return (
        <ChartBox>
          <EmptyState
            title="Mesures pas encore collectées"
            label="Les KPIs BIM apparaîtront dès la première séance de coordination — pas de données à date."
          />
        </ChartBox>
      );
    }
    return (
      <ChartBox className="chart-circular">
        <Radar data={mapped} targets={targets} />
      </ChartBox>
    );
  }

  if (chart.kind === 'sankey') {
    const mapped = safeData
      .filter((d: any) => d?.source != null && d?.target != null)
      .map((d: any) => ({
        source: String(d.source),
        target: String(d.target),
        value: toNumericSafe(d.value ?? 1),
      }));
    if (mapped.length === 0) {
      return <ChartBox><EmptyState label="Aucun flux à afficher." /></ChartBox>;
    }
    return <ChartBox><Sankey links={mapped} /></ChartBox>;
  }
  return <pre style={{ fontSize: 10 }}>{JSON.stringify(chart, null, 2)}</pre>;
}

function EmptyState({ title, label }: { title?: string; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 180,
        padding: '24px 16px',
        color: 'var(--chart-text-muted, #6B7C72)',
        background: 'var(--c-nav, #F0EDE5)',
        border: '1px dashed var(--chart-grid, #DDD8CE)',
        borderRadius: 8,
        fontSize: 12,
      }}
    >
      {title && <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13, color: 'var(--chart-text, #1A2A22)' }}>{title}</div>}
      <div>{label}</div>
    </div>
  );
}

function CaptureResolver({ id }: { id: number }) {
  const [cap, setCap] = useState<{ file_key: string; mime_type: string | null; kind: string } | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ capture: { file_key: string; mime_type: string | null; kind: string } }>(
        `/captures/${id}`
      )
      .then((r) => {
        if (!cancelled) setCap(r.capture);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id]);
  if (!cap) return <div className="card-meta">Capture #{id}…</div>;
  const url = fileUrl(cap.file_key);
  if ((cap.mime_type ?? '').startsWith('video') || cap.kind === 'turntable') {
    return <video src={url} controls autoPlay loop muted />;
  }
  return <img src={url} alt="capture" />;
}

/**
 * Détecte la colonne-unité ("Unité") si elle existe, pour appliquer le bon format
 * (CHF → "CHF 6'090'950", m² → "517,5 m²", % → "85 %").
 */
function detectUnitColumn(cols: string[]): { idx: number; kind: 'currency' | 'percent' | 'unit' } | null {
  const lower = cols.map((c) => String(c ?? '').toLowerCase());
  const unitIdx = lower.findIndex((c) => /^unité|^unite|^unit$/i.test(c));
  if (unitIdx >= 0) return { idx: unitIdx, kind: 'unit' };
  return null;
}

function looksNumeric(v: any): boolean {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  if (!s) return false;
  const cleaned = s.replace(/[\s'’ ]/g, '').replace(',', '.').replace(/[^\d.\-]/g, '');
  if (!cleaned) return false;
  const n = Number(cleaned);
  return Number.isFinite(n);
}
function toNumberStrict(v: any): number {
  const cleaned = String(v ?? '').trim().replace(/[\s'’ ]/g, '').replace(',', '.').replace(/[^\d.\-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

function detectNumericColumns(cols: string[], rows: string[][]): Set<number> {
  const out = new Set<number>();
  for (let j = 0; j < cols.length; j++) {
    let numeric = 0;
    let total = 0;
    for (const r of rows) {
      const v = r?.[j];
      if (v == null || v === '') continue;
      total++;
      if (looksNumeric(v)) numeric++;
    }
    if (total >= 2 && numeric / total >= 0.7) out.add(j);
  }
  return out;
}

/** Format une cellule en fonction de l'entête et/ou de la colonne unité adjacente. */
/**
 * Formatage prioritaire par la colonne "Unité" de la ligne.
 * Le préfixe CHF n'est appliqué QUE si l'unité de la ligne est explicitement CHF (ou CHF/m²).
 * Pour m², m³, ml, pce, etc. on affiche le nombre formaté (l'unité est déjà dans sa propre colonne).
 * Le header de colonne n'est utilisé que si AUCUNE unité n'est disponible pour la ligne.
 */
function formatCell(value: any, colHeader: string, rowUnit?: string): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  if (!looksNumeric(raw)) return raw;
  const n = toNumberStrict(raw);
  if (!Number.isFinite(n)) return raw;

  const unit = String(rowUnit ?? '').trim();

  // 1) Si une unité est présente sur la ligne, elle fait foi.
  if (unit) {
    // Monnaie uniquement quand l'unité est vraiment CHF / CHF/m² / EUR / €
    if (/^(chf|chf\/m²|chf\/m2|eur|€|fr\.?)$/i.test(unit)) {
      // CHF/m² → on garde le format monétaire mais on conserve l'unité composée
      if (/\/m²|\/m2/i.test(unit)) return `${formatCurrency(n, 'CHF')}/m²`;
      return formatCurrency(n, /^(eur|€)$/i.test(unit) ? 'EUR' : 'CHF');
    }
    if (/^%$/.test(unit)) return formatPercent(n);
    // Toute autre unité (m², m³, ml, pce, sem, mois, j, kWh, kWc, …) : nombre + unité
    return `${formatNumber(n)}\u202F${unit}`;
  }

  // 2) Pas d'unité de ligne → on tombe en secours sur l'intitulé de colonne.
  const header = String(colHeader ?? '').toLowerCase();
  if (/%/.test(header) || /pourcent|part/i.test(header)) return formatPercent(n);
  // CHF en fallback SEULEMENT si le header est sans ambiguïté (contient explicitement "chf"/"€"/"eur").
  if (/\bchf\b|€|\beur\b/i.test(header)) return formatCurrency(n, /€|eur/i.test(header) ? 'EUR' : 'CHF');
  // Plus de détection "coût/prix/budget" sans unité explicite → évite le faux CHF sur m²/m³.
  return formatNumber(n);
}

function FilterableTable({ cols, rows }: { cols: string[]; rows: string[][] }) {
  const [sortIdx, setSortIdx] = useState<number | null>(null);
  const [dir, setDir] = useState<1 | -1>(1);
  const [q, setQ] = useState('');
  const numericCols = detectNumericColumns(cols, rows);
  const unitCol = detectUnitColumn(cols);
  const filtered = rows.filter((r) =>
    q ? r.some((c) => String(c).toLowerCase().includes(q.toLowerCase())) : true
  );
  const sorted =
    sortIdx !== null
      ? [...filtered].sort((a, b) => {
          const va = a[sortIdx!] ?? '';
          const vb = b[sortIdx!] ?? '';
          const na = toNumberStrict(va);
          const nb = toNumberStrict(vb);
          if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * dir;
          return String(va).localeCompare(String(vb)) * dir;
        })
      : filtered;
  return (
    <div>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filtrer…"
        style={{
          padding: 6,
          marginBottom: 6,
          borderRadius: 6,
          border: '1px solid var(--c-border)',
          background: 'var(--c-bg)',
          width: '100%',
        }}
      />
      <table className="slide-table">
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th
                key={i}
                style={{ cursor: 'pointer', textAlign: numericCols.has(i) ? 'right' : 'left' }}
                onClick={() => {
                  if (sortIdx === i) setDir((d) => (d === 1 ? -1 : 1));
                  else {
                    setSortIdx(i);
                    setDir(1);
                  }
                }}
              >
                {c}
                {sortIdx === i && <span style={{ marginLeft: 4 }}>{dir === 1 ? '▲' : '▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => {
                const isNum = numericCols.has(j);
                const rowUnit = unitCol ? r[unitCol.idx] : undefined;
                const display = isNum ? formatCell(c, cols[j], rowUnit) : String(c ?? '');
                return (
                  <td
                    key={j}
                    style={{
                      textAlign: isNum ? 'right' : 'left',
                      fontVariantNumeric: isNum ? 'tabular-nums' : undefined,
                      whiteSpace: isNum ? 'nowrap' : undefined,
                    }}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Détecte nb pages + orientation (via ratio largeur/hauteur de la 1re page) avec pdfjs-dist. */
async function detectPdfMetadata(file: File): Promise<{ pages: number; orientation: Orientation }> {
  // Import dynamique — la typage de pdfjs-dist expose /legacy/build/pdf.
  const pdfjs: any = await import('pdfjs-dist');
  try {
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      // pdfjs-dist charge son worker via import.meta.url ; Vite s'en occupe.
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
    }
  } catch { /* le worker peut déjà être configuré ailleurs dans l'app */ }
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pages = doc.numPages || 1;
  const page = await doc.getPage(1);
  const vp = page.getViewport({ scale: 1 });
  const orientation: Orientation = vp.width > vp.height ? 'landscape' : 'portrait';
  try { await doc.destroy?.(); } catch {}
  return { pages, orientation };
}

/**
 * Drawer pour configurer le thème custom :
 *   - Upload d'un PDF (1 ou 2 pages) utilisé comme masque à l'export.
 *   - Orientation détectée automatiquement (verrouille la présentation).
 *   - Sélecteur de police (système + 6 Google Fonts).
 *   - Color picker pour la couleur d'accent.
 * Le PDF est stocké dans R2, la config dans le payload D1 de la présentation.
 */
function CustomThemeDrawer({
  projectId,
  current,
  onClose,
  onChange,
}: {
  projectId: number;
  current: CustomTheme;
  onClose: () => void;
  onChange: (next: CustomTheme) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [accent, setAccent] = useState<string>(current.accent ?? '#3D5A45');
  const [font, setFont] = useState<FontId>(current.font ?? 'system');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setErr('Le masque doit être un PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr('Masque PDF trop lourd (>10 Mo).');
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      // Détection orientation + pages côté client via pdfjs-dist (déjà bundlé).
      const { pages, orientation: detected } = await detectPdfMetadata(file);
      const maskPages: 1 | 2 = pages >= 2 ? 2 : 1;
      const form = new FormData();
      form.append('file', file);
      form.append('pages', String(maskPages));
      form.append('orientation', detected);
      const res = await fetch(
        `/api/v1/projects/${projectId}/synthesis/custom-theme-mask`,
        { method: 'POST', credentials: 'include', body: form }
      );
      if (!res.ok) throw new Error(await res.text());
      const j = (await res.json()) as { mask_key: string };
      onChange({
        ...current,
        mask_key: j.mask_key,
        mask_pages: maskPages,
        mask_orientation: detected,
        font,
        accent,
      });
    } catch (ex: any) {
      setErr(String(ex?.message ?? ex));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="export-modal" onClick={onClose}>
      <div className="export-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <strong>Thème custom</strong>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600 }}>Masque PDF (1 ou 2 pages)</label>
        <div className="card-meta" style={{ fontSize: 11, marginBottom: 6 }}>
          1 page : appliquée à toutes les pages. 2 pages : page 1 = garde, page 2 = intérieur.
          L'orientation est détectée et verrouillée.
        </div>
        <input type="file" accept="application/pdf" onChange={handleFile} disabled={uploading} />
        {current.mask_key && (
          <div className="card-meta" style={{ fontSize: 11, marginTop: 6 }}>
            Masque actuel : {current.mask_pages ?? '?'} page(s), {current.mask_orientation ?? '?'}.
          </div>
        )}

        <div style={{ height: 16 }} />
        <label style={{ fontSize: 12, fontWeight: 600 }}>Police principale</label>
        <select
          value={font}
          onChange={(e) => {
            const next = e.target.value as FontId;
            setFont(next);
            onChange({ ...current, font: next, accent });
          }}
          style={{ width: '100%', padding: 6, marginTop: 4 }}
        >
          {FONT_CHOICES.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>

        <div style={{ height: 16 }} />
        <label style={{ fontSize: 12, fontWeight: 600 }}>Couleur d'accent</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
          <input
            type="color"
            value={accent}
            onChange={(e) => {
              setAccent(e.target.value);
              onChange({ ...current, accent: e.target.value, font });
            }}
            style={{ width: 48, height: 32, padding: 0, border: '1px solid var(--c-border)', borderRadius: 6 }}
          />
          <code style={{ fontSize: 12 }}>{accent}</code>
        </div>

        {err && <div className="error-msg" style={{ marginTop: 14 }}>{err}</div>}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function FullscreenPresenter({
  presentation,
  theme,
  start,
  onClose,
}: {
  presentation: Presentation;
  theme: ThemeId;
  start: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(start);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ')
        setIdx((v) => Math.min(v + 1, (presentation.sections ?? []).length - 1));
      if (e.key === 'ArrowLeft') setIdx((v) => Math.max(v - 1, 0));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [(presentation.sections ?? []).length, onClose]);

  const section = presentation.sections[idx];
  return (
    <div className="present-fullscreen">
      <div
        className={`fs-slide pres-theme-wrap ${themeClass(theme)}`}
        key={idx}
        style={{ padding: 0 }}
      >
        <div
          className={`presentation-slide ${section.type === 'hero' ? 'slide-hero' : ''}`}
          data-section-idx={String(idx + 1).padStart(2, '0')}
        >
          <SlideContent section={section} fullscreen />
        </div>
      </div>
      <div className="fs-nav">
        <button onClick={() => setIdx((v) => Math.max(0, v - 1))}>←</button>
        {(presentation.sections ?? []).map((_, i) => (
          <div
            key={i}
            className={`fs-dot ${idx === i ? 'active' : ''}`}
            onClick={() => setIdx(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
        <button
          onClick={() => setIdx((v) => Math.min((presentation.sections ?? []).length - 1, v + 1))}
        >
          →
        </button>
        <button onClick={onClose}>×</button>
      </div>
    </div>
  );
}
